import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { microsoftSSOLogin } from "../sagas/actions";
import { fetchFreshPixelToken } from "../util/pixelToken";
import {
  clearMicrosoftCodeVerifier,
  clearMicrosoftState,
  getMicrosoftClientId,
  getMicrosoftRedirectUri,
  getMicrosoftScope,
  getMicrosoftTokenUrl,
  readMicrosoftCodeVerifier,
  readMicrosoftState,
} from "../util/microsoftSSOConfig";

const buildErrorMessage = (error, fallback) => {
  if (!error) return fallback;
  if (typeof error === "string") return error;
  return fallback;
};

export default function useMicrosoftSSO({
  enabled,
  code,
  stateParam,
  error,
  errorDescription,
  onComplete,
} = {}) {
  const dispatch = useDispatch();
  const [status, setStatus] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [processing, setProcessing] = useState(false);
  const attemptedRef = useRef(false);

  useEffect(() => {
    if (!enabled || attemptedRef.current) {
      return;
    }
    attemptedRef.current = true;

    const cleanup = () => {
      clearMicrosoftState();
      clearMicrosoftCodeVerifier();
    };

    const finish = (nextStatus, nextError, err) => {
      setStatus(nextStatus || "");
      setErrorMessage(nextError || "");
      onComplete?.({
        status: nextStatus,
        errorMessage: nextError,
        error: err,
      });
      cleanup();
    };

    if (error) {
      const message =
        errorDescription ||
        buildErrorMessage(
          error,
          "Microsoft sign-in was cancelled. Please try again."
        );
      console.info("[MicrosoftSSO] Callback received with error", {
        error,
        errorDescription,
        stateParam,
      });
      finish("Unable to finish Microsoft sign-in.", message);
      return;
    }

    if (!code) {
      console.info("[MicrosoftSSO] Callback missing authorization code", {
        stateParam,
        hasCode: Boolean(code),
      });
      cleanup();
      return;
    }

    const expectedState = readMicrosoftState();
    if (!stateParam || !expectedState || stateParam !== expectedState) {
      finish(
        "Unable to finish Microsoft sign-in.",
        "Your sign-in session expired. Please start the Microsoft sign-in again."
      );
      return;
    }

    const codeVerifier = readMicrosoftCodeVerifier();
    if (!codeVerifier) {
      finish(
        "Unable to finish Microsoft sign-in.",
        "We could not validate your Microsoft sign-in. Please try again."
      );
      return;
    }

    const exchangeForToken = async () => {
      setProcessing(true);
      try {
        setStatus("Contacting Microsoft…");

        console.info("[MicrosoftSSO] Exchanging code for tokens", {
          tokenUrl: getMicrosoftTokenUrl(),
          redirectUri: getMicrosoftRedirectUri(),
          hasCode: Boolean(code),
          stateParam,
          codeVerifierLength: codeVerifier?.length,
        });

        const tokenResponse = await fetch(getMicrosoftTokenUrl(), {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: new URLSearchParams({
            client_id: getMicrosoftClientId(),
            scope: getMicrosoftScope(),
            code,
            redirect_uri: getMicrosoftRedirectUri(),
            grant_type: "authorization_code",
            code_verifier: codeVerifier,
          }),
        });

        const tokenJson = await tokenResponse.json();
        if (!tokenResponse.ok || tokenJson.error) {
          const detail =
            tokenJson.error_description ||
            tokenJson.error ||
            "token_exchange_failed";
          throw new Error(detail);
        }

        const idToken = tokenJson.id_token;
        if (!idToken) {
          throw new Error("missing_id_token");
        }

        let pixelToken = "";
        try {
          pixelToken = await fetchFreshPixelToken();
        } catch (pixErr) {
          console.warn("[MicrosoftSSO] Unable to fetch pixel token", pixErr);
        }

        setStatus("Signing you in…");
        console.info("[MicrosoftSSO] Token exchange successful; dispatching login", {
          hasIdToken: Boolean(idToken),
          tokenKeys: Object.keys(tokenJson || {}),
          hasPixelToken: Boolean(pixelToken),
        });
        dispatch(microsoftSSOLogin({ idToken, pixelToken }));
        onComplete?.({
          status: "Signing you in…",
          errorMessage: "",
          error: null,
        });
      } catch (err) {
        console.error("[MicrosoftSSO] Error completing Microsoft sign-in", err);
        const message =
          err?.message === "missing_id_token"
            ? "We could not verify your Microsoft account. Please try again."
            : "Unable to finish Microsoft sign-in. Please try again.";
        finish("Unable to finish Microsoft sign-in.", message, err);
      } finally {
        setProcessing(false);
        cleanup();
      }
    };

    if (typeof window !== "undefined" && window.history?.replaceState) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    exchangeForToken();
  }, [enabled, code, stateParam, error, errorDescription, dispatch, onComplete]);

  return {
    status,
    errorMessage,
    processing,
  };
}
